import { computed, reactive, ref, toRefs } from 'vue'

import { defineStore } from 'pinia'

import { handleApiErrorToast, isGptAccessRevokedAxiosError } from '@/apiClient'
import { ChatApi } from '@/apiClient/modules/chat'
import {
  applyPinnedChatsToCaches,
  MAX_PINNED_CHATS,
  PINNED_CHATS_COMMIT_DEBOUNCE_MS,
} from '@/apiClient/queries/chatPinCacheUtils'
import type { StreamState } from '@/apiClient/queries/useChatQueries'
import { useChatQueries } from '@/apiClient/queries/useChatQueries'
import type {
  IChat,
  IChatListItem,
  IChatMessage,
  IChatProcessingStep,
} from '@/apiClient/types/chat'
import type { IUploadedFile } from '@/apiClient/types/file'
import type { IMessage } from '@/apiClient/types/messages'
import type {
  ChatStreamPhase,
  IChatStreamEvent,
  IChatToolCallEvent,
} from '@/composables/useEventStream'
import { useMetricEvents } from '@/composables/useMetricEvents'
import { useRetentionClock } from '@/composables/useRetentionClock'
import { PosthogFeatureFlags, usePosthogFlag } from '@/lib/posthog'
import {
  countClosedHtmlFences,
  countClosedMermaidFences,
} from '@/store/preview'
import { trackBackendRedactions } from '@/utils/chdRedactionTracking'
import {
  buildFileGenerationPlaceholderFile,
  FILE_GENERATION_JOBS,
  type FileGenerationJobType,
  isFileGenerationPlaceholderFile,
} from '@/utils/fileGenerationJobs'
import {
  clearPublishedHtmlFileWebsiteMetaForWebsite,
  removePublishedHtmlFileAttachment,
  setPublishedHtmlFileWebsiteMeta,
  upsertPublishedHtmlFileAttachment,
} from '@/utils/publishing'

import { buildAssistantPatchFromMessageCompleteEvent } from './utils/assistantMessageCompletePatch'
import { findAssistantMessageForMermaidMetrics } from './utils/assistantMessageLookup'
import { getChatDeletionInfo } from './utils/chatExpiry'
import {
  chatMessagesWithFetched,
  findChatMessage,
  type FetchedMessagesByChatId,
} from './utils/fetchedMessages'
import { groupChatsByRelativeDate } from './utils/groupChatsByRelativeDate'
import {
  mergePinnedIntoChatList,
  selectDatedChats,
  withChatPinned,
} from './utils/pinnedChats'
import {
  appendProcessingMetaToBuffer,
  drainBufferedProcessingSteps,
  mergeAssistantUiHints,
  normalizeProcessingMetaRow,
  withoutProcessingStep,
} from './utils/pipelineUiHints'
import {
  createProvisionalAssistantMessage,
  findLastAssistantMessageWithoutId,
  shouldAppendPipelineProvisionalAssistant,
} from './utils/provisionalAssistant'
import { applyToolCallEvent } from './utils/toolCallHints'

/** Target upper bound for draining the entire pending buffer (ms). */
const ASSISTANT_STREAM_MAX_DRAIN_MS = 500
/** Below this many pending chars, reveal one code unit per frame for smooth typing. */
const ASSISTANT_STREAM_SINGLE_CHAR_THRESHOLD = 24
const ASSISTANT_STREAM_FRAME_MS_ESTIMATE = 16

type AssistantStreamPumpState = {
  pending: string
  rafId: number | null
  messageId: string
}

/** Edit applied to every message of a chat (see `mapChatMessages`). */
type ChatMessageMapper = (message: IChatMessage) => IChatMessage
/** Edit applied to one assistant message's files (see `updateAssistantMessageFiles`). */
type MessageFilesMapper = (files: IChatMessage['files']) => IUploadedFile[]

/** Dedupes `message_complete` vs extra teardown hooks firing for the same assistant message. */
const mermaidMetricsSentForAssistant = new Set<string>()

function recordAssistantMermaidMetricsOnce(
  chatId: string,
  messageId: string,
  content: string,
) {
  const key = `${chatId}::${messageId}`
  if (mermaidMetricsSentForAssistant.has(key)) return
  mermaidMetricsSentForAssistant.add(key)
  const mermaidCount = countClosedMermaidFences(content)

  for (let ordinal = 0; ordinal < mermaidCount; ordinal++) {
    useMetricEvents().sendEvent('mermaid_generated')
  }
}

/**
 * Tracks how many closed ```html fences we've already emitted `'HTML generated'` for,
 * keyed by `${chatId}::${messageId}`. Lets us fire as soon as the closing ``` arrives
 * during streaming without re-emitting on subsequent buffer pumps/flushes.
 */
const htmlMetricsReportedCountByAssistant = new Map<string, number>()

/**
 * Inspect the cumulative assistant `content` for newly-closed ```html fences and
 * emit `'HTML generated'` once per fence we haven't reported yet.
 *
 * Detection runs against the cumulative content (not the pending buffer) so that
 * a delta split between ``` and html (or flushed mid-fence) is still recognized
 * once both halves and the closing ``` have landed in `msg.content`.
 */
function recordAssistantHtmlMetricsForBuffer(
  chatId: string,
  messageId: string,
  content: string,
) {
  const key = `${chatId}::${messageId}`
  const closedCount = countClosedHtmlFences(content)
  const reported = htmlMetricsReportedCountByAssistant.get(key) ?? 0
  if (closedCount <= reported) return
  for (let ordinal = 0; ordinal < closedCount - reported; ordinal++) {
    useMetricEvents().sendEvent('HTML generated')
  }
  htmlMetricsReportedCountByAssistant.set(key, closedCount)
}

export const useChatStore = defineStore('chat', () => {
  // #region Chat Queries
  const chatQueries = useChatQueries()
  // Page 1, so the response carries the caller's pinned chats beside `items` — the API
  // only fills `pinned` on the first page.
  const { data: chatsListPaginated } = chatQueries.list({
    size: 100,
    is_draft: false,
  })
  // #endregion

  const loaders = reactive({
    isFetchingList: ref(false),
    isFetching: ref(false),
    isGetting: ref(false),
    isCreating: ref(false),
    isUpdating: ref(false),
    isDeleting: ref(false),
    isFetchingOlderMessages: ref(false),
  })
  // TODO: deprecate individual loaders
  const isLoading = ref(false)
  const isLoadingActiveChat = ref(false)
  const isLoadingUpdate = ref(false)
  const isLoadingDelete = ref(false)

  // To track new chats that are created (first message stream)
  const newChatList = ref<IChatListItem[]>([])
  const chatsList = computed(() => [...(chatsListPaginated.value?.items || [])])

  // Reactive clock so expiring-soon bucketing re-evaluates as time passes, not
  // only when the chat list itself changes (see useRetentionClock).
  const retentionNow = useRetentionClock()

  /**
   * Kill switch for pinning as a whole. Off means no "Pinned" section, no pin/unpin
   * option on a row and no reordering — but the chats themselves stay in the sidebar,
   * and whatever the user had pinned is left untouched server-side so flipping the
   * flag back restores it.
   */
  const isChatPinningEnabled = usePosthogFlag(PosthogFeatureFlags.ChatPinning)

  /**
   * Chats the user pinned to the top of the sidebar, in the order they stored (see
   * `PATCH /chats/pins`), not by date. They arrive beside the paginated list rather than
   * inside it, which is what lets a pinned chat stay visible even when it is older than
   * the window `chatsList` covers.
   */
  const serverPinnedChats = computed(
    () => chatsListPaginated.value?.pinned || [],
  )
  /**
   * Empty while the feature is switched off, which is what hides the "Pinned" section,
   * drops the pin marker from every row and stops `isChatPinned` holding chats out of
   * the dated list below (see `selectDatedChats`).
   */
  const pinnedChats = computed(() =>
    isChatPinningEnabled.value ? serverPinnedChats.value : [],
  )
  const pinnedChatIds = computed(
    () => new Set(pinnedChats.value.map((chat) => chat.id)),
  )
  /**
   * Pinned rows are rendered by the sidebar's own "Pinned" section, so they must not
   * also appear in the dated list below it. The API already excludes them from `items`;
   * this also covers the locally-held `newChatList`, and a pin whose optimistic cache
   * write has landed ahead of the refetch.
   */
  const isChatPinned = (chat: Pick<IChatListItem, 'id'>) =>
    pinnedChatIds.value.has(chat.id)
  /** True once the user is at the sidebar's pinned-chat cap. */
  const hasReachedPinnedChatsLimit = computed(
    () => pinnedChats.value.length >= MAX_PINNED_CHATS,
  )

  /**
   * `PATCH /chats/pins` replaces the caller's whole list, so every writer has to send a
   * complete one — and two writers that each build it from the list they last *read*
   * silently drop each other's work (pin two chats quickly, keep only the second).
   *
   * So both writers go through here. The cache is updated **synchronously**, making it
   * the single record of what the user wants, and the request sends whatever that record
   * says at the moment it goes out — never a copy captured earlier.
   */
  let pendingPinnedCommit: ReturnType<typeof setTimeout> | undefined

  const cancelPendingPinnedCommit = () => {
    if (pendingPinnedCommit === undefined) return
    clearTimeout(pendingPinnedCommit)
    pendingPinnedCommit = undefined
  }

  /** Sends the pinned list as it stands now, absorbing any scheduled send. */
  const commitPinnedChats = () => {
    cancelPendingPinnedCommit()
    return chatQueries.setPins.mutateAsync({ chats: pinnedChats.value })
  }

  /**
   * Applies `chats` as the new pinned order and defers the request, so a burst of drags
   * costs one. Re-reading the list at send time is what lets a pin taken mid-window ride
   * along instead of being overwritten by a stale order.
   */
  const reorderPinnedChats = (chats: IChatListItem[]) => {
    applyPinnedChatsToCaches(chats)
    cancelPendingPinnedCommit()
    pendingPinnedCommit = setTimeout(() => {
      pendingPinnedCommit = undefined
      void commitPinnedChats()
    }, PINNED_CHATS_COMMIT_DEBOUNCE_MS)
  }

  /**
   * Pins or unpins one chat, sending at once — the user just clicked, and the request
   * carries any drag order still sitting in the debounce window with it. Callers should
   * check {@link hasReachedPinnedChatsLimit} first so the cap can be explained; the
   * optimistic write means a second click already sees the first one's result.
   */
  const setChatPinned = (chat: IChatListItem, isPinned: boolean) => {
    applyPinnedChatsToCaches(withChatPinned(pinnedChats.value, chat, isPinned))
    return commitPinnedChats()
  }

  const combinedChatList = computed(() => {
    const newChatIds = new Set(newChatList.value.map((chat) => chat.id))
    const regularChats: IChatListItem[] = [...newChatList.value]
    const expiringChats: IChatListItem[] = []

    selectDatedChats(
      isChatPinningEnabled.value,
      serverPinnedChats.value,
      chatsList.value,
    ).forEach((chat) => {
      if (newChatIds.has(chat.id)) return
      if (isChatPinned(chat)) return

      const deletionInfo = getChatDeletionInfo(chat, retentionNow.value)
      if (deletionInfo?.isExpiringSoon) {
        expiringChats.push(chat)
      } else {
        regularChats.push(chat)
      }
    })

    return [...regularChats, ...expiringChats]
  })

  const activeChatId = ref<IChat['id']>('')
  const setActiveChatId = (id: IChat['id']) => {
    activeChatId.value = id
  }
  const activeChat = computed(() => chatMapById.value[activeChatId.value])

  /**
   * Optimistic chat rendered by the ChatLoading page while POST `/chats` is in
   * flight, so sending from the home page lands on the chat view immediately
   * instead of waiting for `chat_created`. Its `id` is a local-only placeholder
   * id, never sent to the API, and it is discarded once the real chat arrives.
   */
  const placeholderChat = ref<IChat>()
  const setPlaceholderChat = (chat?: IChat) => {
    placeholderChat.value = chat
  }

  /**
   * Drops a placeholder chat and the per-chat state keyed by its local-only id.
   * Deletes the map entries rather than setting them false so placeholder ids
   * don't accumulate across chat creations. Only clears `placeholderChat` when it
   * is still the one named, so a late cleanup can't wipe a newer send's.
   */
  const clearPlaceholderChat = (placeholderId: string) => {
    delete chatIsThinkingMap.value[placeholderId]
    delete chatIsRespondingMap.value[placeholderId]
    delete newChatStreamStateMap.value[placeholderId]
    if (placeholderChat.value?.id === placeholderId) {
      placeholderChat.value = undefined
    }
  }

  /**
   * List of chats (no messages)
   */
  const chatMapById = ref<Record<IChat['id'], IChat>>({})
  const chatIsThinkingMap = ref<Record<IChat['id'], boolean>>({})
  const chatIsRespondingMap = ref<Record<IChat['id'], boolean>>({})
  const chatIsReasoningMap = ref<Record<IChat['id'], boolean>>({})
  /** True from an `llm_retry` until the model answers again (or the wait ends). */
  const chatIsRetryingMap = ref<Record<IChat['id'], boolean>>({})
  const chatStreamPhaseMap = ref<Record<IChat['id'], ChatStreamPhase>>({})
  /**
   * When the reply's rotating wait copy started (epoch ms): the moment the model was first
   * called. Every live indicator for the chat reads the copy from this point, so it carries on
   * when the indicator moves into the steps list or a wait resumes after a step. Cleared once
   * the reply stops responding.
   */
  const chatWaitCopyStartedAtMap = ref<Record<IChat['id'], number>>({})
  // @deprecated
  const chatRespondingTaskIdMap = ref<Record<IChat['id'], string>>({})
  /**
   * Map to track streamStates for newChat streams by chat ID
   */
  const newChatStreamStateMap = ref<Record<IChat['id'], StreamState>>({})
  /**
   * Messages fetched individually (see `ensureMessageLoaded`) because they sit outside
   * the loaded window, kept per chat beside `chat.messages` (see FetchedMessagesByChatId).
   */
  const fetchedMessagesByChatId = ref<FetchedMessagesByChatId>({})

  const addNewChatToList = (chat: IChatListItem) => {
    newChatList.value = [chat, ...newChatList.value]
  }
  const removeNewChatFromList = (chatId: string) => {
    newChatList.value = newChatList.value.filter(
      (chatListItem) => chatListItem.id !== chatId,
    )
  }
  const getChatById = (id: IChat['id']) => chatMapById.value[id]

  /**
   * One message of a chat, whether it is in the loaded window or was fetched on its
   * own (`ensureMessageLoaded`). Read publish state / HTML / `publishable_html_endpoints`
   * through this so a page from an unloaded message resolves like any loaded one.
   */
  const getChatMessage = (chatId: IChat['id'], messageId: string) =>
    findChatMessage(
      getChatById(chatId)?.messages,
      fetchedMessagesByChatId.value[chatId],
      messageId,
    )

  /** Every message the store holds for a chat — loaded window plus individually fetched, oldest first. */
  const getChatMessagesInStore = (chatId: IChat['id']) =>
    chatMessagesWithFetched(
      getChatById(chatId)?.messages,
      fetchedMessagesByChatId.value[chatId],
    )

  /**
   * Replaces one message wherever the store holds it — the loaded window, the
   * individually fetched copy, or both — with `updateMessage`'s result, so an edit
   * (e.g. publish state on `message.files`) also reaches a page whose message was
   * fetched on its own. Returning the message unchanged (same object) is a no-op.
   */
  const updateChatMessage = (
    chatId: IChat['id'],
    messageId: string,
    updateMessage: ChatMessageMapper,
  ) => {
    const windowMessages = getChatById(chatId)?.messages
    const index =
      windowMessages?.findIndex((message) => message.id === messageId) ?? -1
    if (windowMessages && index !== -1) {
      const updated = updateMessage(windowMessages[index])
      if (updated !== windowMessages[index]) windowMessages[index] = updated
    }

    const fetchedMessages = fetchedMessagesByChatId.value[chatId]
    const fetched = fetchedMessages?.[messageId]
    if (fetchedMessages && fetched) {
      const updated = updateMessage(fetched)
      if (updated !== fetched) fetchedMessages[messageId] = updated
    }
  }

  /** `updateChatMessage` narrowed to the `files` of one assistant message. */
  const updateAssistantMessageFiles = (
    chatId: IChat['id'],
    messageId: string,
    mapFiles: MessageFilesMapper,
  ) =>
    updateChatMessage(chatId, messageId, (message) => {
      if (message.role !== 'assistant') return message
      const files = mapFiles(message.files)
      return files === message.files ? message : { ...message, files }
    })

  /**
   * Applies `mapMessage` across the whole chat — loaded window *and* individually
   * fetched messages. For editing a single known message use `updateChatMessage`;
   * this is for sweeps that can touch any message (see `clearAssistantHtmlBlocksForWebsite`).
   */
  const mapChatMessages = (
    chatId: IChat['id'],
    mapMessage: ChatMessageMapper,
  ) => {
    const chatInStore = getChatById(chatId)
    if (chatInStore) {
      let changed = false
      const nextMessages = chatInStore.messages.map((message) => {
        const mapped = mapMessage(message)
        if (mapped !== message) changed = true
        return mapped
      })
      if (changed) chatInStore.messages = nextMessages
    }

    const fetchedMessages = fetchedMessagesByChatId.value[chatId]
    if (!fetchedMessages) return
    for (const [messageId, message] of Object.entries(fetchedMessages)) {
      const mapped = mapMessage(message)
      if (mapped !== message) fetchedMessages[messageId] = mapped
    }
  }

  /**
   * Every chat the sidebar holds, newest first (see `mergePinnedIntoChatList`). Reads
   * the server's pinned chats rather than the flag-gated `pinnedChats`, so search still
   * finds them when pinning is switched off.
   */
  const allChats = computed(() =>
    mergePinnedIntoChatList(serverPinnedChats.value, chatsList.value),
  )

  const chatsByDateGroups = computed(() =>
    groupChatsByRelativeDate(
      allChats.value,
      newChatList.value,
      retentionNow.value,
    ),
  )

  const setChat = (chat: IChat) => {
    const existingChatMessages = chatMapById.value[chat.id]?.messages || []
    chatMapById.value[chat.id] = {
      ...chat,
      messages: chat.messages?.length ? chat.messages : existingChatMessages,
    }
  }

  const markChatGptAccessRevoked = (chatId: string) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    chatInStore.gpt_access_revoked = true
  }

  /** Drops the optimistic user row added before POST …/messages when the stream fails before persisting. */
  const removeLastOptimisticUserMessage = (chatId: string) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    const last = chatInStore.messages[chatInStore.messages.length - 1]
    if (last?.role === 'user' && !last?.id) {
      chatInStore.messages.pop()
    }
  }

  /** Merge publish response into `message.files` (same shape as API `html_block_{n}.html`). */
  const markAssistantHtmlBlockPublished = (
    chatId: string,
    messageId: string,
    blockIndex: number,
    publishedUrl: string,
  ) =>
    updateAssistantMessageFiles(chatId, messageId, (files) =>
      upsertPublishedHtmlFileAttachment(files, blockIndex, publishedUrl),
    )

  /** Remove published HTML attachment from `message.files` after unpublish. */
  const markAssistantHtmlBlockUnpublished = (
    chatId: string,
    messageId: string,
    blockIndex: number,
  ) =>
    updateAssistantMessageFiles(chatId, messageId, (files) =>
      removePublishedHtmlFileAttachment(files, blockIndex),
    )

  /**
   * Stamp the linked-website metadata on a block's published HTML file (keeps the
   * file). Used to keep publish state in sync without a chat refetch: on publish set
   * `{websiteId, isPublished:true}`; on unpublish keep the file + `websiteId` and set
   * `isPublished:false` (matches backend EVGPT-1532). Consumers read publish state
   * from `file.metadata`.
   */
  const setAssistantHtmlBlockWebsite = (
    chatId: string,
    messageId: string,
    blockIndex: number,
    website: { websiteId: string; isPublished: boolean },
  ) =>
    updateAssistantMessageFiles(chatId, messageId, (files) =>
      setPublishedHtmlFileWebsiteMeta(files, blockIndex, website),
    )

  /**
   * Drop the linked-website metadata from every assistant html_block file across
   * the chat that points at `websiteId`, except the block in `keep`. Called after a
   * block is (re)published onto a website another block previously owned (override),
   * so the evicted blocks stop rendering "Published" without a chat refetch
   * (EVGPT-1474). Consumers read publish state from `file.metadata`.
   */
  const clearAssistantHtmlBlocksForWebsite = (
    chatId: string,
    websiteId: string,
    keep?: { messageId: string; blockIndex: number },
  ) =>
    mapChatMessages(chatId, (message) => {
      if (message.role !== 'assistant' || !Array.isArray(message.files)) {
        return message
      }
      const files = clearPublishedHtmlFileWebsiteMetaForWebsite(
        message.files,
        websiteId,
        keep && message.id === keep.messageId ? keep.blockIndex : undefined,
      )
      return files === message.files ? message : { ...message, files }
    })

  const setChatUserMessage = (chatId: string, chatMessage: IChatMessage) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return

    if (
      chatInStore.messages.some(
        (existingMessage) =>
          !!chatMessage.id && existingMessage.id === chatMessage.id,
      )
    )
      return

    chatInStore.messages.push({
      id: chatMessage.id,
      role: 'user',
      content: chatMessage.content,
      model: chatMessage.model,
      timestamp: chatMessage.timestamp,
      files: chatMessage.files,
      doGenerateImage: chatMessage.doGenerateImage,
    })
  }

  /**
   * Re-applies the client-only image-generation flag after `chat_created`, where
   * the server's chat object replaces the local placeholder and drops it.
   */
  const markLatestUserDoGenerateImage = (chatId: string) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    const userMsg = [...chatInStore.messages]
      .reverse()
      .find((message) => message.role === 'user')
    if (userMsg) {
      userMsg.doGenerateImage = true
    }
  }

  /**
   * Clears the optimistic image-generation flag on the latest user message that still has it.
   * Called when `message_complete` arrives for an image background job so the typing placeholder
   * moves to the assistant row.
   */
  const clearLatestUserDoGenerateImage = (chatId: string) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    const userMsg = [...chatInStore.messages]
      .reverse()
      .find((message) => message.role === 'user' && message.doGenerateImage)
    if (userMsg) {
      userMsg.doGenerateImage = false
    }
  }

  /** Provisional assistant row created by the stream before `message_id` is known. */
  const resolveAssistantMessageWithoutId = (chatInStore: IChat) =>
    findLastAssistantMessageWithoutId(chatInStore.messages)

  /**
   * Applies the full `message_complete` SSE payload onto the assistant row: merges every field
   * from `event.message` and from the event root except envelope keys and `content`; `content`
   * is applied only when `updateContent` is true (image generation, or a saved message with
   * content). Links a provisional assistant row by id or appends a new one when missing.
   */
  const applyAssistantMessageCompleteFromEvent = (
    event: IChatStreamEvent,
    opts: { updateContent: boolean; imageJobId?: string },
  ) => {
    const effectiveId = event.message?.id ?? event.message_id
    if (!effectiveId) return

    const chatInStore = getChatById(event.chat_id)
    if (!chatInStore) return

    const patch = buildAssistantPatchFromMessageCompleteEvent(event)

    const applyPatchToTarget = (target: IChatMessage) => {
      for (const key of Object.keys(patch)) {
        if (key === 'content') continue
        const value = patch[key]
        if (value !== undefined) {
          ;(target as unknown as Record<string, unknown>)[key] = value
        }
      }
      if (opts.updateContent) {
        const next =
          event.message?.content !== undefined
            ? event.message.content
            : event.content
        if (next !== undefined) {
          target.content = next
        }
      }
    }

    let msg = chatInStore.messages.find((message) => message.id === effectiveId)
    if (!msg) {
      const provisional = resolveAssistantMessageWithoutId(chatInStore)
      if (provisional) {
        provisional.id = effectiveId
        msg = provisional
      }
    }

    if (!msg) {
      const newMsg: IChatMessage = {
        id: effectiveId,
        role: 'assistant',
        content: '',
        model: '',
        timestamp: 0,
      }
      applyPatchToTarget(newMsg)
      chatInStore.messages.push(newMsg)
    } else {
      applyPatchToTarget(msg)
    }

    if (opts.imageJobId) {
      const target = chatInStore.messages.find(
        (message) => message.id === effectiveId,
      )
      if (target) {
        const job = {
          job_id: opts.imageJobId,
          job_type: 'image_generation',
          status: 'running',
        }
        const jobs = target.pending_jobs ?? []
        if (!jobs.some((pendingJob) => pendingJob.job_id === opts.imageJobId)) {
          target.pending_jobs = [...jobs, job]
        }
      }
    }
  }

  const assistantStreamPumpByChatId: Record<string, AssistantStreamPumpState> =
    {}

  /**
   * Reactive SSE buffer for `processing_meta` (merged on each `setChatResponseMessage`).
   * `takeBufferedProcessingSteps` / `clearStreamingProcessingSteps` stay here because they
   * assign this ref; pure map logic lives in `./utils/pipelineUiHints`.
   */
  const streamingProcessingStepsByChatId = ref<
    Record<string, IChatProcessingStep[]>
  >({})

  /** Removes and returns buffered `processing_meta` rows for this chat (SSE buffer). */
  function takeBufferedProcessingSteps(chatId: string): IChatProcessingStep[] {
    const { map: next, drained } = drainBufferedProcessingSteps(
      streamingProcessingStepsByChatId.value,
      chatId,
    )
    if (drained.length > 0) {
      streamingProcessingStepsByChatId.value = next
    }
    return drained
  }

  /**
   * Ensures an empty assistant bubble exists as soon as pipeline meta or a reasoning delta
   * arrives, so those can render before the first content chunk (ChatBubble only mounts
   * assistant UI when a row exists).
   */
  function ensureProvisionalAssistantForStreaming(chatId: string) {
    const streamActive = !!(
      chatIsThinkingMap.value[chatId] || chatIsRespondingMap.value[chatId]
    )
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    if (
      !shouldAppendPipelineProvisionalAssistant(
        chatInStore.messages,
        streamActive,
      )
    ) {
      return
    }
    const last = chatInStore.messages[chatInStore.messages.length - 1]
    const now = Math.floor(Date.now() / 1000)
    chatInStore.messages.push(
      createProvisionalAssistantMessage(last.model ?? '', now),
    )
  }

  /** Clears SSE pipeline buffer (e.g. abort); does not change message rows. */
  function clearStreamingProcessingSteps(chatId: string) {
    takeBufferedProcessingSteps(chatId)
  }

  /**
   * Buffers one `processing_meta` SSE row; `setChatResponseMessage` merges the buffer
   * into the assistant row for the current stream (avoids attaching to a previous reply).
   */
  const appendProcessingMeta = (step: IChatProcessingStep) => {
    if (!getChatById(step.chat_id)) return
    ensureProvisionalAssistantForStreaming(step.chat_id)
    const row = normalizeProcessingMetaRow(step, Math.floor(Date.now() / 1000))
    streamingProcessingStepsByChatId.value = appendProcessingMetaToBuffer(
      streamingProcessingStepsByChatId.value,
      row,
    )
  }

  const flushAssistantStreamBuffer = (chatId: string) => {
    const state = assistantStreamPumpByChatId[chatId]
    if (!state) return
    if (state.rafId != null) {
      cancelAnimationFrame(state.rafId)
      state.rafId = null
    }
    if (!state.pending) {
      delete assistantStreamPumpByChatId[chatId]
      return
    }
    const chatInStore = getChatById(chatId)
    if (chatInStore) {
      const msg = chatInStore.messages.find(
        (message) => message.id === state.messageId,
      )
      if (msg) {
        msg.content += state.pending
        recordAssistantHtmlMetricsForBuffer(
          chatId,
          state.messageId,
          msg.content,
        )
      }
    }
    state.pending = ''
    delete assistantStreamPumpByChatId[chatId]
  }

  const runAssistantStreamPump = (chatId: string) => {
    const state = assistantStreamPumpByChatId[chatId]
    if (!state) return

    const pump = () => {
      const pumpState = assistantStreamPumpByChatId[chatId]
      if (!pumpState || pumpState.pending.length === 0) {
        if (pumpState) {
          pumpState.rafId = null
          if (!pumpState.pending) delete assistantStreamPumpByChatId[chatId]
        }
        return
      }

      const chatInStore = getChatById(chatId)
      if (!chatInStore) {
        pumpState.pending = ''
        pumpState.rafId = null
        delete assistantStreamPumpByChatId[chatId]
        return
      }

      const msg = chatInStore.messages.find(
        (message) => message.id === pumpState.messageId,
      )
      if (!msg) {
        pumpState.pending = ''
        pumpState.rafId = null
        delete assistantStreamPumpByChatId[chatId]
        return
      }

      const backlog = pumpState.pending.length
      const chunkSize =
        backlog <= ASSISTANT_STREAM_SINGLE_CHAR_THRESHOLD
          ? 1
          : Math.max(
              1,
              Math.ceil(
                (backlog * ASSISTANT_STREAM_FRAME_MS_ESTIMATE) /
                  ASSISTANT_STREAM_MAX_DRAIN_MS,
              ),
            )

      msg.content += pumpState.pending.slice(0, chunkSize)
      pumpState.pending = pumpState.pending.slice(chunkSize)
      recordAssistantHtmlMetricsForBuffer(
        chatId,
        pumpState.messageId,
        msg.content,
      )

      if (pumpState.pending.length > 0) {
        pumpState.rafId = requestAnimationFrame(pump)
      } else {
        pumpState.rafId = null
        delete assistantStreamPumpByChatId[chatId]
      }
    }

    state.rafId = requestAnimationFrame(pump)
  }

  const enqueueAssistantStreamDelta = (
    chatId: string,
    messageId: string,
    delta: string,
  ) => {
    if (!delta) return
    let state = assistantStreamPumpByChatId[chatId]
    if (!state) {
      state = { pending: '', rafId: null, messageId }
      assistantStreamPumpByChatId[chatId] = state
    }
    state.messageId = messageId
    state.pending += delta
    if (state.rafId == null) {
      runAssistantStreamPump(chatId)
    }
  }

  const setChatResponseMessage = (chatEvent: IChatStreamEvent) => {
    const chatInStore = getChatById(chatEvent.chat_id)
    if (!chatInStore) return

    /** Backend may send id on `message` only; optimistic user rows have no `id`. */
    const effectiveMessageId = chatEvent.message_id ?? chatEvent.message?.id

    let updatedMessage: IChatMessage | undefined
    if (effectiveMessageId) {
      updatedMessage = chatInStore.messages.find(
        (message) => message.id === effectiveMessageId,
      )
    }

    if (!updatedMessage && !effectiveMessageId) {
      // Replace message if message_complete event only if message_id does not exist
      if (chatEvent.message) {
        const mid = chatEvent.message.id
        const doesMessageIdExist =
          mid != null &&
          chatInStore.messages.some((message) => message.id === mid)
        if (!doesMessageIdExist) chatInStore.messages.push(chatEvent.message)
        updatedMessage =
          mid != null
            ? chatInStore.messages.find((message) => message.id === mid)
            : chatEvent.message
      } else {
        updatedMessage = resolveAssistantMessageWithoutId(chatInStore)
      }
    }

    // First `message_chunk` may include `message_id` while `stream_info` already created a row without id.
    if (!updatedMessage && effectiveMessageId) {
      const provisional = resolveAssistantMessageWithoutId(chatInStore)
      if (provisional) {
        provisional.id = effectiveMessageId
        updatedMessage = provisional
      }
    }

    const replaceContent =
      chatEvent.content_replace !== undefined &&
      chatEvent.content_replace !== ''
        ? chatEvent.content_replace
        : undefined

    let skipContentDelta = false
    if (replaceContent !== undefined && updatedMessage) {
      const pumpState = assistantStreamPumpByChatId[chatEvent.chat_id]
      if (pumpState) {
        if (pumpState.rafId != null) {
          cancelAnimationFrame(pumpState.rafId)
        }
        delete assistantStreamPumpByChatId[chatEvent.chat_id]
      }
      updatedMessage.content = replaceContent
      skipContentDelta = true
    }

    const delta = skipContentDelta ? '' : (chatEvent.content ?? '')

    if (!updatedMessage) {
      const newMessageId = effectiveMessageId ?? chatEvent.message_id
      const modelFromEvent = chatEvent.model ?? chatEvent.message?.model ?? ''
      let initialUiHints: IChatMessage['ui_hints']
      if (chatEvent.clear_processing_step) {
        takeBufferedProcessingSteps(chatEvent.chat_id)
        initialUiHints = undefined
      } else {
        initialUiHints = mergeAssistantUiHints(
          undefined,
          chatEvent.ui_hints ?? chatEvent.message?.ui_hints,
          takeBufferedProcessingSteps(chatEvent.chat_id),
        )
      }
      chatInStore.messages.push({
        id: newMessageId,
        role: 'assistant',
        content: replaceContent ?? '',
        model: modelFromEvent,
        files: chatEvent.files ?? chatEvent.message?.files,
        timestamp:
          chatEvent.timestamp ??
          chatEvent.message?.timestamp ??
          Math.floor(Date.now() / 1000),
        citations: chatEvent.citations ?? chatEvent.message?.citations,
        ui_hints: initialUiHints,
      })

      if (newMessageId && delta) {
        enqueueAssistantStreamDelta(chatEvent.chat_id, newMessageId, delta)
      }
      return
    }

    if (delta) {
      if (updatedMessage.id) {
        enqueueAssistantStreamDelta(chatEvent.chat_id, updatedMessage.id, delta)
      } else {
        updatedMessage.content += delta
      }
    }
    const incomingModel = chatEvent.model ?? chatEvent.message?.model
    if (incomingModel && !updatedMessage.model) {
      updatedMessage.model = incomingModel
    }

    // Add files
    if (chatEvent.files) {
      updatedMessage.files = chatEvent.files
    }

    if (updatedMessage.role === 'assistant') {
      if (chatEvent.clear_processing_step) {
        takeBufferedProcessingSteps(chatEvent.chat_id)
        updatedMessage.ui_hints = withoutProcessingStep(
          updatedMessage.ui_hints,
          chatEvent.clear_processing_step,
        )
      } else {
        const drained = takeBufferedProcessingSteps(chatEvent.chat_id)
        updatedMessage.ui_hints = mergeAssistantUiHints(
          updatedMessage.ui_hints,
          chatEvent.ui_hints,
          drained,
        )
        if (chatEvent.message?.ui_hints !== undefined) {
          updatedMessage.ui_hints = chatEvent.message.ui_hints
        }
      }
    }
  }

  /** After flush / stream end: emit `mermaid_generated` once per closed Mermaid code fence in the reply. */
  const recordAssistantStreamMermaidMetrics = (
    chatId: string,
    messageId?: string,
  ) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    const msg = findAssistantMessageForMermaidMetrics(
      chatInStore.messages,
      messageId,
    )
    if (!msg) return
    const id = msg.id ?? `noid:${chatId}:${msg.timestamp}`
    recordAssistantMermaidMetricsOnce(chatId, id, msg.content)
  }

  const flushAssistantStreamBufferThenRecordMermaidMetrics = (
    chatId: string,
    messageId?: string,
  ) => {
    flushAssistantStreamBuffer(chatId)
    recordAssistantStreamMermaidMetrics(chatId, messageId)
  }

  const fetchChat = async (chatId: string) => {
    const existingChat = chatMapById.value[chatId]
    // Always fetch initial 20 messages when navigating to a chat
    // This ensures we have the latest messages even if chat exists in store
    loaders.isFetching = true
    return ChatApi.get(chatId, { messages_limit: 20 })
      .then((chat) => {
        trackBackendRedactions(chatId, chat.messages)
        if (existingChat) {
          // If any existing messages have no id, replace all messages
          if (existingChat.messages.some((msg) => !msg.id)) {
            existingChat.messages = chat.messages
            return
          }

          // Merge with existing messages, deduplicating by ID
          const existingMessageIds = new Set(
            existingChat.messages.map((msg) => msg.id),
          )
          const newMessages = chat.messages.filter(
            (msg) => !existingMessageIds.has(msg.id),
          )

          // If we have new messages, merge them
          if (newMessages.length > 0) {
            existingChat.messages = [...existingChat.messages, ...newMessages]
          }

          // Update other chat properties
          Object.assign(existingChat, {
            ...chat,
            messages: existingChat.messages,
          })
          return existingChat
        } else {
          setChat(chat)
          return chat
        }
      })
      .finally(() => (loaders.isFetching = false))
  }

  const OLDER_MESSAGES_PAGE_SIZE = 20

  const loadOlderMessages = async (chatId: string) => {
    if (loaders.isFetchingOlderMessages) {
      return Promise.resolve({ loadedCount: 0 })
    }

    const chatInStore = getChatById(chatId)
    if (!chatInStore) {
      return Promise.resolve({ loadedCount: 0 })
    }

    // API is assumed to return messages newest-first; offset = number of newest to skip
    const messagesOffset = chatInStore.messages.length

    loaders.isFetchingOlderMessages = true
    return ChatApi.get(chatId, {
      messages_limit: OLDER_MESSAGES_PAGE_SIZE,
      messages_offset: messagesOffset,
    })
      .then((chat) => {
        // If no messages returned, we've reached the beginning
        if (chat.messages.length === 0) {
          return { loadedCount: 0 }
        }

        // Prepend older messages to existing messages
        // Messages come in reverse order when offset > 0, so we need to reverse them
        const olderMessages = [...chat.messages]

        // Deduplicate messages by ID to prevent loading the same message twice
        const existingMessageIds = new Set(
          chatInStore.messages.map((msg) => msg.id),
        )
        const newMessages = olderMessages.filter(
          (msg) => !existingMessageIds.has(msg.id),
        )

        if (newMessages.length > 0) {
          chatInStore.messages = [...newMessages, ...chatInStore.messages]
        }

        return {
          loadedCount: newMessages.length,
        }
      })
      .finally(() => (loaders.isFetchingOlderMessages = false))
  }

  /** In-flight `ensureMessageLoaded` fetches, so parallel callers share one request. */
  const messageFetchesInFlight = new Map<string, Promise<boolean>>()

  /**
   * Makes `messageId` readable through `getChatMessage` / `getChatMessagesInStore`,
   * fetching it on its own when it sits outside the loaded window — e.g. to render and
   * publish an HTML page selected from an older message. Resolves true once the message
   * is available, false when it can't be fetched (unknown chat or message → 404).
   *
   * The single-message endpoint enriches the message exactly like GET `/chats/{id}`
   * does (signed file URLs with `file.metadata`, `ui_hints`, `publishable_html_endpoints`,
   * feedback), so callers read title / publish state / publish path from it with no
   * special-casing. The message is cached beside the chat instead of being spliced into
   * `chat.messages` — see FetchedMessagesByChatId for why that window must stay
   * contiguous.
   */
  const ensureMessageLoaded = async (chatId: string, messageId: string) => {
    if (!chatId || !messageId) return false
    if (getChatMessage(chatId, messageId)) return true

    const fetchKey = `${chatId}::${messageId}`
    const inFlight = messageFetchesInFlight.get(fetchKey)
    if (inFlight) return inFlight

    const request = ChatApi.getMessage(chatId, messageId)
      .then((message) => {
        // Keyed by the requested id (what callers look it up by); an id-less message
        // would be skipped by every consumer anyway, so treat it as not found.
        if (!message?.id) return false
        fetchedMessagesByChatId.value[chatId] = {
          ...fetchedMessagesByChatId.value[chatId],
          [messageId]: message,
        }
        return true
      })
      .catch(() => false)
      .finally(() => messageFetchesInFlight.delete(fetchKey))

    messageFetchesInFlight.set(fetchKey, request)
    return request
  }

  /**
   * A stream phase only means something while the chat is thinking, so leaving “thinking”
   * drops it here rather than at every stream terminator (`message_chunk`, `message_complete`,
   * abort, error, stream end). Those are five call sites that would each have to remember;
   * this is one that cannot be forgotten. `chatIsReasoningMap` rides along for the same
   * reason: it only drives the typing indicator, which is mounted only while thinking.
   */
  /**
   * Messages the server appended to a chat while a reply was streaming there, held until that
   * reply is done. The streaming reply has to stay the chat's last row (its live state, tool
   * calls and question card all go by that), and the server stores such a message after it.
   */
  const deferredChatMessagesByChatId: Record<string, IChatMessage[]> = {}

  const isChatStreamActive = (chatId: string) =>
    !!(chatIsThinkingMap.value[chatId] || chatIsRespondingMap.value[chatId])

  const pushChatMessageIfMissing = (chatId: string, message: IChatMessage) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    if (chatInStore.messages.some((existing) => existing.id === message.id)) {
      return
    }
    chatInStore.messages.push(message)
  }

  /** Once the chat's reply is done streaming, lists the messages held back for it. */
  const listDeferredChatMessages = (chatId: string) => {
    if (isChatStreamActive(chatId)) return
    const deferred = deferredChatMessagesByChatId[chatId]
    if (!deferred) return
    delete deferredChatMessagesByChatId[chatId]
    deferred.forEach((message) => pushChatMessageIfMissing(chatId, message))
  }

  /**
   * Lists a message the server appended to the chat outside a stream (e.g. the delivery text a
   * finished file job writes after its reply) at the end of the chat, where a reload has it.
   * While a reply is streaming in the chat, the message waits and follows that reply.
   */
  const appendChatMessage = (chatId: string, message: IChatMessage) => {
    if (isChatStreamActive(chatId)) {
      ;(deferredChatMessagesByChatId[chatId] ??= []).push(message)
      return
    }
    pushChatMessageIfMissing(chatId, message)
  }

  const setChatIsThinking = (chatId: string, isThinking: boolean) => {
    chatIsThinkingMap.value[chatId] = isThinking
    if (!isThinking) {
      delete chatStreamPhaseMap.value[chatId]
      delete chatIsReasoningMap.value[chatId]
      delete chatIsRetryingMap.value[chatId]
      listDeferredChatMessages(chatId)
    }
  }

  /** Marks the model request as being retried; cleared once the model answers again. */
  const setChatIsRetrying = (chatId: string, isRetrying: boolean) => {
    if (isRetrying) {
      chatIsRetryingMap.value[chatId] = true
    } else {
      delete chatIsRetryingMap.value[chatId]
    }
  }

  /**
   * Records one `mcp_tool_call_start` / `mcp_tool_call_end` on the streaming assistant row, in
   * the `ui_hints.mcp_tool_calls` shape `message_complete` later brings back. The events carry
   * no message id; the row they belong to is the chat's last message, created here when the
   * tool runs before any text.
   *
   * A starting tool also puts the chat back into “thinking”: the model may have streamed some
   * text before calling it, and the wait that follows needs a live indicator like any other.
   * It ends the reasoning phase too — the model has moved on to acting; a later round that
   * reasons again sets it back.
   */
  const recordAssistantToolCall = (event: IChatToolCallEvent) => {
    const chatInStore = getChatById(event.chat_id)
    if (!chatInStore) return

    ensureProvisionalAssistantForStreaming(event.chat_id)
    const streamingMessage =
      chatInStore.messages[chatInStore.messages.length - 1]
    if (streamingMessage?.role !== 'assistant') return

    streamingMessage.ui_hints = {
      ...streamingMessage.ui_hints,
      version: streamingMessage.ui_hints?.version ?? 1,
      mcp_tool_calls: applyToolCallEvent(
        streamingMessage.ui_hints?.mcp_tool_calls ?? [],
        event,
      ),
    }
    setChatIsRetrying(event.chat_id, false)
    if (event.status === 'started') {
      delete chatIsReasoningMap.value[event.chat_id]
      setChatIsThinking(event.chat_id, true)
    }
  }

  /**
   * Appends one `reasoning_chunk` delta onto the streaming assistant row. The row is the single
   * source of truth for reasoning — `message_complete` later overwrites `reasoning_content` with
   * the backend's assembled text, so there is no separate per-chat buffer to keep in sync.
   */
  const appendAssistantReasoningChunk = (chatEvent: IChatStreamEvent) => {
    const delta = chatEvent.content ?? ''
    if (!delta || !chatEvent.chat_id) return

    const chatInStore = getChatById(chatEvent.chat_id)
    if (!chatInStore) return

    chatIsReasoningMap.value[chatEvent.chat_id] = true
    setChatIsRetrying(chatEvent.chat_id, false)

    // Reasoning usually streams before the first `message_chunk`, so the assistant row may not
    // exist yet; ChatBubble only mounts assistant UI once it does.
    ensureProvisionalAssistantForStreaming(chatEvent.chat_id)

    const effectiveMessageId = chatEvent.message_id ?? chatEvent.message?.id
    let updatedMessage: IChatMessage | undefined
    if (effectiveMessageId) {
      updatedMessage = chatInStore.messages.find(
        (message) => message.id === effectiveMessageId,
      )
    }
    if (!updatedMessage) {
      updatedMessage = resolveAssistantMessageWithoutId(chatInStore)
    }
    if (!updatedMessage) return

    updatedMessage.reasoning_content =
      (updatedMessage.reasoning_content ?? '') + delta
  }

  /**
   * Records the pre-stream milestone driving the typing indicator's label.
   *
   * Seeded with `request_received` the moment a message is sent — not only from SSE — because
   * “thinking” starts one round trip before the backend's first `stream_progress` lands. Without
   * the seed the label opens on the no-phase fallback (“Thinking”), drops to “Preparing” when the
   * event arrives, then returns to “Thinking”: a visible step backwards.
   */
  const setChatStreamPhase = (chatId: string, phase: ChatStreamPhase) => {
    chatStreamPhaseMap.value[chatId] = phase
    // Later tool rounds call the model again; the copy keeps counting from the first call.
    if (
      phase === 'calling_model' &&
      chatWaitCopyStartedAtMap.value[chatId] === undefined
    ) {
      chatWaitCopyStartedAtMap.value[chatId] = Date.now()
    }
  }

  const setChatIsResponding = (chatId: string, isResponding: boolean) => {
    chatIsRespondingMap.value[chatId] = isResponding
    if (!isResponding) {
      delete chatWaitCopyStartedAtMap.value[chatId]
      listDeferredChatMessages(chatId)
    }
  }

  const setNewChatStreamState = (
    chatId: string,
    streamState: StreamState | null | undefined,
  ) => {
    if (streamState) {
      newChatStreamStateMap.value[chatId] = streamState
    } else {
      delete newChatStreamStateMap.value[chatId]
    }
  }

  const getNewChatStreamState = (chatId: string) => {
    return newChatStreamStateMap.value[chatId]
  }

  /**
   * Adds a placeholder IUploadedFile (status: 'processing') to a specific assistant message while
   * a background file-generation job runs. The ChatBubble renders it through the normal files code
   * path, giving a loading spinner via UploadedFile's status-based isLoadingFile logic.
   */
  const markAssistantMessageFileGenerationPending = (
    chatId: string,
    messageId: string,
    jobType: FileGenerationJobType,
  ) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    const msg = chatInStore.messages.find(
      (message) => message.role === 'assistant' && message.id === messageId,
    )
    if (!msg) return
    if (
      (msg.files ?? []).some((file) =>
        isFileGenerationPlaceholderFile(file, jobType),
      )
    )
      return
    msg.files = [
      ...(msg.files ?? []),
      buildFileGenerationPlaceholderFile(jobType, messageId),
    ]
  }

  /**
   * Removes the job's placeholder file from a specific assistant message.
   * Called on job failure; the success path swaps the placeholder for the delivered file.
   */
  const clearFileGenerationPlaceholder = (
    chatId: string,
    messageId: string,
    jobType: FileGenerationJobType,
  ) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    const msg = chatInStore.messages.find(
      (message) => message.role === 'assistant' && message.id === messageId,
    )
    if (!msg) return
    msg.files = (msg.files ?? []).filter(
      (file) => !isFileGenerationPlaceholderFile(file, jobType),
    )
  }

  /**
   * Swaps a failed file-generation job's last progress for its failure, so the tool row reads
   * why the file never came rather than the last heartbeat (or "Queued…").
   */
  const markFileGenerationJobFailed = (
    chatId: string,
    messageId: string,
    jobType: FileGenerationJobType,
    error?: string | null,
  ) => {
    const { progressStep, failureMessage } = FILE_GENERATION_JOBS[jobType]
    const msg = getChatById(chatId)?.messages.find(
      (message) => message.role === 'assistant' && message.id === messageId,
    )
    const hints = msg?.ui_hints
    if (!hints?.processing_steps) return
    hints.processing_steps = hints.processing_steps.map((step) =>
      step.step === progressStep
        ? {
            ...step,
            data: {
              title: failureMessage,
              outcome: error ?? '',
            },
          }
        : step,
    )
  }

  /**
   * Retires the `image_generation` entry from an assistant row's `pending_jobs`,
   * which is what ends the placeholder. Required on failure: no file ever arrives,
   * so nothing else would clear it.
   */
  const clearImageGenerationJob = (chatId: string, messageId: string) => {
    const chatInStore = getChatById(chatId)
    if (!chatInStore) return
    const msg = chatInStore.messages.find(
      (message) => message.role === 'assistant' && message.id === messageId,
    )
    if (!msg?.pending_jobs) return
    msg.pending_jobs = msg.pending_jobs.filter(
      (job) => job.job_type !== 'image_generation',
    )
  }

  ////////// Legacy code below //////////

  const setChatWebSearch = (
    chatId: string,
    messageId: string,
    webSearch: IMessage['webSearch'],
  ) => {
    const chat = chatMapById.value[chatId]
    if (!chat) return
    const message = chat.messages?.find((message) => message.id === messageId)
    if (message) {
      // Legacy: webSearch property may not exist on IChatMessage
      // Type assertion needed for legacy compatibility
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(message as any).webSearch = webSearch
    }
  }

  /** Updates title on the loaded chat and provisional sidebar row only; list query cache is patched from the SSE layer (`new_title`). */
  const setChatTitle = (id: string, title: string) => {
    const chatInStore = getChatById(id)
    if (chatInStore) {
      chatInStore.title = title
    }

    const provisional = newChatList.value.find((c) => c.id === id)
    if (provisional) {
      provisional.title = title
    }
  }

  const updateChat = (id: string, data: Partial<IChat>) => {
    if (isLoadingUpdate.value) return Promise.resolve()

    isLoadingUpdate.value = true
    return ChatApi.update(id, data)
      .then((_chats) => {
        // fetchChatList()

        return _chats
      })
      .catch((err) => {
        if (isGptAccessRevokedAxiosError(err)) {
          markChatGptAccessRevoked(id)
        }
        return handleApiErrorToast(err)
      })
      .finally(() => (isLoadingUpdate.value = false))
  }

  const deleteChat = (id: string) => {
    if (isLoadingDelete.value) return Promise.resolve()

    isLoadingDelete.value = true
    return ChatApi.remove(id)
      .then(() => {
        // fetchChatList()
      })
      .catch(handleApiErrorToast)
      .finally(() => (isLoadingDelete.value = false))
  }

  return {
    ...toRefs(loaders),

    isLoading,
    isLoadingUpdate,
    isLoadingDelete,
    isLoadingActiveChat,
    chatsList,
    isChatPinningEnabled,
    pinnedChats,
    isChatPinned,
    hasReachedPinnedChatsLimit,
    setChatPinned,
    reorderPinnedChats,
    chatMapById,
    chatIsThinkingMap,
    chatIsRespondingMap,
    chatIsReasoningMap,
    chatIsRetryingMap,
    chatStreamPhaseMap,
    chatWaitCopyStartedAtMap,
    chatRespondingTaskIdMap,
    chatsByDateGroups,
    activeChatId,
    activeChat,
    placeholderChat,
    combinedChatList,
    setPlaceholderChat,
    clearPlaceholderChat,
    setActiveChatId,
    fetchChat,
    loadOlderMessages,
    ensureMessageLoaded,
    // fetchChatList,
    updateChat,
    deleteChat,
    setChat,
    markChatGptAccessRevoked,
    removeLastOptimisticUserMessage,
    markAssistantHtmlBlockPublished,
    markAssistantHtmlBlockUnpublished,
    setAssistantHtmlBlockWebsite,
    clearAssistantHtmlBlocksForWebsite,
    setChatUserMessage,
    markLatestUserDoGenerateImage,
    clearLatestUserDoGenerateImage,
    clearImageGenerationJob,
    applyAssistantMessageCompleteFromEvent,
    setChatResponseMessage,
    appendProcessingMeta,
    clearStreamingProcessingSteps,
    streamingProcessingStepsByChatId,
    flushAssistantStreamBuffer,
    flushAssistantStreamBufferThenRecordMermaidMetrics,
    recordAssistantStreamMermaidMetrics,
    newChatList,
    addNewChatToList,
    removeNewChatFromList,
    getChatById,
    getChatMessage,
    getChatMessagesInStore,
    setChatWebSearch,
    setChatTitle,
    setChatIsThinking,
    appendAssistantReasoningChunk,
    recordAssistantToolCall,
    setChatIsRetrying,
    setChatIsResponding,
    setChatStreamPhase,
    setNewChatStreamState,
    getNewChatStreamState,
    newChatStreamStateMap,
    markAssistantMessageFileGenerationPending,
    clearFileGenerationPlaceholder,
    markFileGenerationJobFailed,
    appendChatMessage,
  }
})
